import { RefObject, useCallback, useImperativeHandle, useState } from "react";

import { Button, IconButton, Modal, Tooltip, Typography } from "@mui/material";
import classNames from "classnames";
import moment, { Moment } from "moment";
import { useForm, useWatch } from "react-hook-form";
import { IoClose } from "react-icons/io5";
import { toast } from "react-toastify";

import { defaultTimezone } from "../../../../../constants/timezone";
import { useAccount } from "../../../../../hooks/useAccount";
import { useSchedule } from "../../../../../hooks/useSchedule";
import { DatePicker } from "../../../../common/DatePicker/DatePicker";
import { TimePicker } from "../../../../common/TimePicker/TimePicker";

export type TAddBusyTimeFieldValues = {
	selectedStartTime: string;
	selectedEndTime: string;
	selectedStartDate: Moment | null;
	selectedEndDate: Moment | null;
};

export type TAddBusyTimeModalProps = {
	ref: RefObject<TAddBusyTimeModalRef | null>;
};
export type TAddBusyTimeModalRef = {
	open: (option?: {
		startDate?: Date;
		startTime?: Date;
		endTime?: Date;
	}) => void;
	close: () => void;
};

export function AddBusyTimeModal(props: TAddBusyTimeModalProps) {
	const { account } = useAccount();
	const [isAddBusyTimeModalOpen, setIsAddBusyTimeModalOpen] = useState(false);
	const minDate = moment().hour(0).minute(0).second(0).millisecond(0);
	const maxDate = moment()
		.hour(0)
		.minute(0)
		.second(0)
		.millisecond(0)
		.add(13, "day");

	const { bookings, busyTimes, addBusyTime } = useSchedule();

	const { control, setValue, handleSubmit } = useForm<TAddBusyTimeFieldValues>({
		defaultValues: {
			selectedStartTime: "",
			selectedEndTime: "",
			selectedStartDate: null,
			selectedEndDate: null,
		},
	});

	const {
		selectedStartDate,
		selectedEndDate,
		selectedStartTime,
		selectedEndTime,
	} = useWatch({ control }) as {
		selectedStartDate: Moment | null;
		selectedEndDate: Moment | null;
		selectedStartTime: string;
		selectedEndTime: string;
	};

	const open = useCallback(
		(option?: { startDate?: Date; startTime?: Date; endTime?: Date }) => {
			if (option?.startDate) {
				const startDate = moment(option.startDate)
					.hour(0)
					.minute(0)
					.second(0)
					.millisecond(0);
				if (startDate.isBefore(minDate)) {
					setValue("selectedStartDate", minDate);
				} else if (startDate.isAfter(maxDate)) {
					setValue("selectedStartDate", maxDate);
				} else {
					setValue("selectedStartDate", startDate);
				}
			}
			if (option?.startTime) {
				const startTime = moment(option.startTime);
				setValue(
					"selectedStartTime",
					`${startTime.get("hour") * 60 + startTime.get("minute")}`,
				);
			}
			if (option?.endTime) {
				const endTime = moment(option.endTime);
				setValue(
					"selectedEndTime",
					`${endTime.get("hour") * 60 + endTime.get("minute")}`,
				);
			}
			setIsAddBusyTimeModalOpen(true);
		},
		[maxDate, minDate, setValue],
	);

	const close = useCallback(() => {
		setValue("selectedStartTime", "");
		setValue("selectedEndTime", "");
		setValue("selectedStartDate", null);
		setValue("selectedEndDate", null);
		setIsAddBusyTimeModalOpen(false);
	}, [setValue]);

	useImperativeHandle(
		props.ref,
		() => ({
			open,
			close,
		}),
		[close, open],
	);

	const onClickSaveBusyTime = (data: TAddBusyTimeFieldValues) => {
		let numDates = 1;
		if (
			data.selectedEndDate &&
			data.selectedEndDate.format("YYYY-MM-DD") !== "Invalid date"
		) {
			numDates = data.selectedEndDate.diff(data.selectedStartDate, "day") + 1;
		}

		for (let i = 0; i < numDates; i++) {
			const startTime = moment(data.selectedStartDate)
				.tz(account?.timezoneName ?? defaultTimezone, true)
				.add(i, "day")
				.add(Number(data.selectedStartTime), "minute");

			const endTime = moment(data.selectedStartDate)
				.tz(account?.timezoneName ?? defaultTimezone, true)
				.add(i, "day")
				.add(Number(data.selectedEndTime), "minute");

			const found = [...bookings, ...busyTimes].some((item) => {
				if (moment(item.startTime).isSameOrAfter(endTime)) {
					return false;
				}

				if (moment(item.endTime).isSameOrBefore(startTime)) {
					return false;
				}

				return true;
			});

			if (found) {
				toast.error("Looks like this time is already busy");
				return;
			}
		}

		addBusyTime({
			startDate: moment(data.selectedStartDate)
				.tz(account?.timezoneName ?? defaultTimezone, true)
				.add(Number(data.selectedStartTime), "minute")
				.toISOString(),
			endDate: moment(data.selectedStartDate)
				.tz(account?.timezoneName ?? defaultTimezone, true)
				.add(numDates - 1, "day")
				.add(Number(data.selectedEndTime), "minute")
				.toISOString(),
			blockTime: Number(data.selectedEndTime) - Number(data.selectedStartTime),
		});
		close();
	};

	const calculateIsDisabled = () => {
		if (!selectedStartDate || !selectedStartTime || !selectedEndTime) {
			return true;
		}

		if (selectedStartDate.format("YYYY-MM-DD") === "Invalid date") {
			return true;
		}

		if (selectedEndDate) {
			if (selectedEndDate.format("YYYY-MM-DD") === "Invalid date") {
				return true;
			}
		}

		if (Number(selectedStartTime) >= Number(selectedEndTime)) {
			return true;
		}

		return false;
	};

	const isSaveButtonDisabled = calculateIsDisabled();

	return (
		<Modal
			open={isAddBusyTimeModalOpen}
			onClose={close}
			componentsProps={{
				backdrop: { className: "add-busy-time-modal-backdrop" },
				root: { className: "add-busy-time-modal" },
			}}>
			<div className="modal-content-container">
				<div className="add-busy-time-modal-header">
					<Tooltip title="Close">
						<IconButton className="icon-button" onClick={close}>
							<IoClose />
						</IconButton>
					</Tooltip>
				</div>
				<div className="add-busy-time-modal-body">
					<div className="date-time-picker-container">
						<DatePicker
							minDate={minDate}
							control={control}
							name="selectedStartDate"
							setFormValue={setValue}
						/>
						<Typography className="normal-text time-separator">-</Typography>
						<DatePicker
							minDate={moment(selectedStartDate)}
							maxDate={moment(selectedStartDate).add(13, "day")}
							disabled={
								selectedStartDate === null ||
								selectedStartDate.format("YYYY-MM-DD") === "Invalid date"
							}
							placeholder="Optional"
							control={control}
							name="selectedEndDate"
							setFormValue={setValue}
						/>
					</div>
					<div className="date-time-picker-container">
						<TimePicker control={control} name="selectedStartTime" />
						<div className="time-separator">
							<Typography className="normal-text">-</Typography>
						</div>
						<TimePicker control={control} name="selectedEndTime" />
					</div>
					<div className="control-button-group">
						<Button
							variant="contained"
							className={classNames("normal-button control-button", {
								"disabled-button": isSaveButtonDisabled,
								"black-button": !isSaveButtonDisabled,
							})}
							disabled={isSaveButtonDisabled}
							onClick={handleSubmit(onClickSaveBusyTime)}>
							Block time
						</Button>
					</div>
				</div>
			</div>
		</Modal>
	);
}
